import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPublicPlans } from '../lib/apiClient';
import { plansKeys } from '../lib/api/plans.gen';
import { useAuth } from '../lib/auth';
import { useSubscription } from '../hooks/useSubscription';
import type { Plan } from '../types';
import { pricingPlans } from '../lib/marketing';
import { PlanCompare } from '../components/common/PlanCompare';
import { Grid, Stack } from '../components/ui';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const fallbackPlans: Plan[] = pricingPlans.map((plan) => ({
  id: plan.name,
  slug: plan.price === 'Free trial'
    ? 'trial'
    : plan.name === 'Enterprise'
    ? 'custom'
    : plan.name.toLowerCase(),
  name: plan.name,
  description: plan.description,
  priceMonthly: plan.price === 'Free trial' || plan.price === 'Talk to us' ? null : Number(plan.price.replace(/[^0-9.]/g, '')),
  currency: 'usd',
  features: plan.perks,
  stripePriceId: null,
  trialDays: plan.price === 'Free trial' ? 7 : 0,
  isPublic: true,
  isCustom: plan.name === 'Enterprise',
  organizationId: null,
  active: true,
}));

export function PricingPage(): JSX.Element {
  const { user } = useAuth();
  // Generated `plans.gen.ts` factory (G-19). `fetchPublicPlans` has no
  // generated client function yet (`/subscriptions/plans` predates story 2-92
  // — see `lib/query/subscriptionKeys.ts`), but the CACHE KEY still comes from
  // the generated `plansKeys` factory so this doesn't collide with
  // `PlanCompare`'s `plansKeys.detail('compare')` (same module namespace,
  // different resource).
  const { data: plans = [], isLoading } = useQuery({
    queryKey: plansKeys.list({ scope: 'public' }),
    queryFn: fetchPublicPlans,
    staleTime: 1000 * 60 * 60,
  });

  // Story 2-54: this public route is still reachable authenticated, so the
  // same `['subscription', orgId]` cache key a subscription-less org hits
  // elsewhere (Dashboard/Billing/Sidebar, story 2-32) can land here too — the
  // shared hook's 404→null handling keeps this route from throwing an
  // uncaught rejection for that org.
  const { data: subscription } = useSubscription(user?.organizationId);

  const displayedPlans = useMemo<Plan[]>(() => {
    if (plans.length > 0) {
      return plans;
    }
    return fallbackPlans;
  }, [plans]);

  return (
    <div className="space-y-12">
      <header className="space-y-4 text-center">
        <span className="badge-soft">Memberships</span>
        <h1 className="text-4xl font-semibold text-foreground">Choose the DefectSnap experience that fits your team</h1>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
          Begin with a free export to feel the polish. Upgrade for richer collaboration, concierge support, and integrations that keep your portfolio humming.
        </p>
      </header>

      <Grid columns={{ base: 1, md: 3 }} gap={6}>
        {isLoading && plans.length === 0
          ? [...Array(3).keys()].map((key) => <div key={key} className="glass-card h-64 animate-pulse" />)
          : displayedPlans.map((plan) => {
              const isCurrent = subscription ? subscription.plan === plan.slug : false;
              const priceLabel =
                plan.priceMonthly != null ? `${currencyFormatter.format(plan.priceMonthly)} / mo` : plan.isCustom ? 'Custom pricing' : 'Free';
              const isCustom = plan.isCustom || plan.priceMonthly == null;
              const isTrial = plan.slug === 'trial' || plan.slug === 'starter';
              const ctaLabel = isCurrent ? 'Current plan' : isCustom ? 'Talk to us' : isTrial ? 'Start for free' : 'Upgrade to Pro';
              const features = plan.features ?? [];
              const targetHref = isCurrent
                ? '/app/billing'
                : user && !isCustom
                ? '/app/billing'
                : isTrial
                ? '/signup'
                : `/signup?plan=${plan.slug}`;
              return (
                <Stack key={plan.id} gap={4} align="stretch" className="glass-card h-full p-6">
                  <div>
                    <p className="text-xs uppercase tracking-eyebrow text-muted-foreground">{plan.name}</p>
                    <h2 className="text-2xl font-semibold text-foreground">{priceLabel}</h2>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>
                  {isCurrent && (
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      Current plan
                    </span>
                  )}
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {features.map((feature) => (
                      <li key={feature}>• {feature}</li>
                    ))}
                  </ul>
                  {isCustom ? (
                    <a
                      href="mailto:raykundan57@gmail.com"
                      target="_blank"
                      rel="noreferrer"
                      className="focus-ring mt-auto inline-flex items-center justify-center rounded-full border border-primary/40 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                    >
                      {ctaLabel}
                    </a>
                  ) : (
                    <Link
                      to={targetHref}
                      className="focus-ring mt-auto inline-flex items-center justify-center rounded-full border border-primary/40 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                    >
                      {ctaLabel}
                    </Link>
                  )}
                </Stack>
              );
            })}
      </Grid>

      <section className="glass-card space-y-4 p-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Compare all features</h2>
          <p className="text-sm text-muted-foreground">Exactly what each plan includes — generated from live entitlements, never out of date.</p>
        </div>
        <PlanCompare />
      </section>

      <section className="glass-card space-y-3 p-6 text-sm text-muted-foreground">
        <h2 className="text-lg font-semibold text-foreground">Billing capabilities</h2>
        <Grid as="ul" columns={{ base: 1, sm: 2 }} gap={2}>
          <li>Stripe-hosted checkout and customer portal for upgrades and cancellations</li>
          <li>Metered usage tracking for report exports and PDF generation</li>
          <li>Annual billing discounts with automatic proration</li>
          <li>GST, VAT, and tax invoices handled by Stripe</li>
        </Grid>
      </section>
    </div>
  );
}
